import { useMemo } from 'react';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';

import { useUpdatePropertyMutation } from '../../services/propertyApi';
import { showAlert } from '../../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import {
  buildUpdatePropertyRequest,
  getSubmitErrorMessage,
  rentReadySelection,
  isListedSelection,
  isActiveSelection,
} from '../../add-property/hooks/propertyFormUtils';
import { cityValidation } from '../../add-property/addressValidationSchema';

import type { PropertyEntity } from '../../../../types/property.types';
import type { PropertyDetailFormValues, PropertyDetailMode } from '../types/property-detail.types';

interface UsePropertyDetailFormParams {
  property: PropertyEntity;
  setMode: (next: PropertyDetailMode) => void;
}

const buildInitialValues = (p: PropertyEntity): PropertyDetailFormValues => ({
  PropertyType: p.propertyType ?? '',
  address: p.address1 ?? '',
  other: p.unitNumber ?? '',
  city: p.city ?? '',
  state: p.state ?? '',
  zipCode: p.zipCode ?? '',
  entity: p.entity ?? '',
  entityUUID: p.entityUUID ?? '',
  // Seed the edit form with the customer's PMS identifier only. Legacy
  // rows without `clientPropertyId` start with an empty input so the
  // backend UUID can't be saved into the customer-ID field by mistake.
  clientPropertyId: p.clientPropertyId ?? '',
  bedrooms: p.bedrooms ?? 0,
  bathrooms: p.bathrooms ?? 0,
  // Entity has `squareFeet` (number); form field is `squareFoot` (string)
  squareFoot: p.squareFeet != null ? String(p.squareFeet) : '',
  propertyStatus: p.propertyStatus ?? '',
  portfolioName: p.portfolioName ?? '',
  // Entity has `propertyGroupName`; form field is `propertyGroup`
  propertyGroup: p.propertyGroupName ?? '',
  // Same as clientPropertyId — customer-supplied unit identifier only;
  // legacy rows start empty so the backend UUID isn't promoted into the
  // clientUnitId field on save.
  clientUnitId: p.clientUnitId ?? '',
  rentReady: rentReadySelection(p.rentReady),
  leaseStatus: p.leaseStatus ?? '',
  isListed: isListedSelection(p.isListed),
  isActive: isActiveSelection(p.isActive),
  availableDate: p.availableDate ?? '',
  leaseStartDate: p.leaseStartDate ?? '',
  leaseEndDate: p.leaseEndDate ?? '',
  noticeGivenDate: p.noticeGivenDate ?? '',
  moveInDate: p.moveInDate ?? '',
  moveOutDate: p.moveOutDate ?? '',
  leaseSignedDate: p.leaseSignedDate ?? '',
  listingDate: p.listingDate ?? '',
  latitude: p.latitude ?? '',
  longitude: p.longitude ?? '',
  country: p.country ?? '',
});

const validationSchema = yup.object().shape({
  PropertyType: yup.string().required('Please select property type'),
  address: yup.string().required('Please enter property address details'),
  city: cityValidation,
  state: yup
    .string()
    .required('Please select state')
    .matches(/^[a-zA-Z\s]*$/, 'Please enter letters and spaces only'),
  zipCode: yup
    .string()
    .required('Please enter zip code')
    .matches(/(^\d{5}$)|(^\d{5}-\d{4}$)/, 'Please enter valid zip-code'),
  squareFoot: yup
    .string()
    .max(6, 'Maximum 6 digits allowed')
    .matches(/^\d*\.?\d*$/, 'Only numbers allowed'),
});

export function usePropertyDetailForm({ property, setMode }: UsePropertyDetailFormParams) {
  const dispatch = useDispatch();
  const [updateProperty] = useUpdatePropertyMutation();

  const initialValues = useMemo(() => buildInitialValues(property), [property]);

  const formik = useFormik<PropertyDetailFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      setMode('saving');
      try {
        const body = buildUpdatePropertyRequest(values);
        await updateProperty({ propId: property.propId, body }).unwrap();
        dispatch(showAlert({ type: Variants.SUCCESS, message: 'Changes saved.' }));
        setMode('view');
      } catch (error: unknown) {
        const rtkError = error as { data?: { message?: string } };
        const code: string = rtkError?.data?.message ?? '';
        const errResult = getSubmitErrorMessage(code);
        if (errResult) {
          dispatch(showAlert({
            type: errResult.type === 'error' ? Variants.ERROR : Variants.DEFAULT,
            message: errResult.message,
          }));
        } else {
          dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
        }
        setMode('edit');
      }
    },
  });

  return formik;
}
